import React, { Fragment, useState, useRef, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { GIButton } from 'app-components';
import ModalHeader from '../../../common/headers/ModalHeader';
import UniversalInput from '../../../../lib/wrappers/UniversalInput';
import Form from '../../../../lib/wrappers/Form';
import ValidationErrorMessage from '../../../../components/ssap/notifications/ValidationErrorMessage';
import { VALIDATION_ERROR_MESSAGES } from '../../../../constants/inputs/validationErrorMessages';
import { dashboardModalTypes } from '../../../../constants/individualPortal/dashboard/modals/dashboardModalTypes';
import { commonModalTypes as ModalTypes } from '../../../../constants/common/modals/commonModalTypes';
import { environmentSelector } from '../../../../selectors/commonSelectors';
import { executeCallbacks } from '../../../../utils/miscellaneous/executeCallbacks';
import { preventerTools } from '../../../../utils/tools/preventerTools';
import { phoneNumberTools } from '../../../../utils/tools/phoneNumberTools';

const OTP_LENGTH = 6;

const ContactChangeModal = (props) => {
    const {
        actions: { showModal, otpSend, otpVerify },
        modal: {
            modalData: {
                data,
                name,
                value: defaultValue,
                type,
                validationType,
                updateData,
                callbackOnClose
            }
        },
        methods,
        modalHeaderProps,
        ssapForm,
    } = props;

    const { encryptedHouseholdId, impersonation, impersonationDetails } = useSelector(environmentSelector);
    const [otpStep, setOtpStep] = useState(!type);
    const [value, setValue] = useState(defaultValue ?? data[name] ?? "");
    const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(''));
    const [otpError, setOtpError] = useState('');
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
    const [trackedValues, tracker] = useState(null);

    const isPhone = name === "phoneNumber";
    const channel = isPhone ? "SMS" : "EMAIL";
    const contactKey = isPhone ? "phone" : "email";
    const formattedValue = isPhone ? phoneNumberTools.getAdjustedPhoneNumber(value) : value;
    const form_name = 'contactChangeForm';
    const otp_form_name = 'otpVerifyForm';
    const SUBMIT_CLICK = 'onFormSubmit';
    const OTP_SUCCESS = 'modals.body.otp_success';
    const OTP_VERIFY_FAILED = 'modals.body.otp_verify_fail';
    const OTP_SEND_FAILED = 'modals.body.otp_send_fail';

    const intlApi = useIntl();
    const contactValue = intlApi.formatMessage({ id: isPhone ? 'ssap.input.label.phone_number' : 'ssap.input.label.email' },);
    const resendViaCallText = intlApi.formatMessage({ id: 'modals.body.resend_via_call', defaultMessage: ' ' }).trim();

    const {
        isEligiblyForSave
    } = preventerTools.provideObject(ssapForm[form_name]);

    const handleModalClose = () => {
        executeCallbacks(callbackOnClose);
        modalHeaderProps.hideModal()
    }

    const handleFailureModal = (modalData = {}) => {
        showModal({
            modalType: dashboardModalTypes.FAILURE_MODAL,
            modalData
        })
    }

    const handleSuccessModal = (callback, saveResult) => {
        showModal({
            modalType: ModalTypes.SAVE_RESULT_MODAL,
            modalData: {
                callback,
                saveResult,
            },
        });
    };


    const onContactChange = (...args) => {
        const [, , , [, value]] = args;
        setValue(value);
    };

    const handleChange = (index, e) => {
        const value = e.target.value.replace(/\D/g, '');

        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);

        if (value && index < otp.length - 1) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handleKeyDown = (index, e) => {
        setOtpError('');
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
             inputRefs.current[index - 1]?.focus(); // Move focus to the previous input
             const newOtp = [...otp];
             newOtp[index] = '';
             setOtp(newOtp);
         }
    };

    const handlePaste = (e) => {
        setOtpError('');
        const pasteData = e.clipboardData.getData('text');
        const pastedDigits = pasteData.replace(/\D/g, '').split('');
        const newOtp = [...otp];
        if (pastedDigits.length <= otp.length) {
            for (let i = 0; i < pastedDigits.length; i++) {
                newOtp[i] = pastedDigits[i];
            }

            // Clear any remaining fields
            for (let i = pastedDigits.length; i < otp.length; i++) {
                newOtp[i] = '';
            }

            setOtp(newOtp);
            // Move the focus to the first empty input or last filled input
            inputRefs.current[pastedDigits.length - 1]?.focus();
        }
        else {
            const newOtp = pastedDigits.slice(0, otp.length);
            setOtp(newOtp);
            inputRefs.current[otp.length - 1]?.focus();
        }
    };

    const handleClick = (index) => {
        inputRefs.current[index]?.focus();
        inputRefs.current[index]?.setSelectionRange(0, 1);
    };

    const sendOTP = async (payload) => {
        const res = await otpSend(payload);
        return ({
            errorMessage: res?.message,
            success: res?.success || res?.status === 'SUCCESS',
        });
    }

    const verifyOTP = async (updatedData, successMessage) => {
        const res = await otpVerify(updatedData);
        return ({
            errorMessage: res?.message,
            success: res?.success || res?.status === 'SUCCESS',
            successMessage
        });
    }

    useEffect(() => {
        if (otpStep) {
            onOTPSend();
        }
    }, [])

    const onOTPSend = async () => {
        if (value) {
            const saveResult = await sendOTP({
                householdId: !impersonation ? encryptedHouseholdId : impersonationDetails.encryptedHouseholdId,
                channel,
                [contactKey]: value,
            });

            if (saveResult) {
                setOtpStep(true);
                !saveResult.success && handleFailureModal({ failureMessage: saveResult.errorMessage ?? OTP_SEND_FAILED });
            } else {
                handleFailureModal({ failureMessage: OTP_SEND_FAILED });
            }
        }
    };

    const onContactSubmit = () => {
        type && methods[form_name][SUBMIT_CLICK]();
        if (isEligiblyForSave && defaultValue !== value) {
            onOTPSend();
        }
    }

    const onOTPVerify = async () => {
        const verifyResult = await verifyOTP({
            householdId: !impersonation ? encryptedHouseholdId : impersonationDetails.encryptedHouseholdId,
            otp: otp.join(''),
            [contactKey]: value
        }, OTP_SUCCESS)

        if (verifyResult?.success) {
            handleSuccessModal(handleModalClose, verifyResult);
            updateData({ ...data, [name]: value }, name, value);
        } else {
            setOtpError(OTP_VERIFY_FAILED);
        }
    };

    const checkIfInvalid = () => {
        if(trackedValues === null) {
            return { invalid: true };                    // Button is disabled until User starts changing the input
        }

        const { valid, value } = trackedValues;

        switch (true) {
            case !value:                    // Button is disabled if the value is empty
            case valid === false:           // Button is disabled until we get valid value from input
                return ({ invalid: true, errorKey: VALIDATION_ERROR_MESSAGES[validationType] });
            case value === defaultValue:    // Button is disabled if the value after changes becomes the same as the default value
                return ({ invalid: true }); //Need to confirm message from PM 
            default:
                return ({ invalid: false });
        }
    };

    const updateContactStep = () => {
        return (
            <div>
                <div className='gi-modal__body'>
                    <FormattedMessage id='modals.body.contact_change' values={{ contact: contactValue.toLowerCase() }} />

                    <div className="gi-modal__input" >
                        <Form
                            {...{
                                formName: form_name,
                                formClasses: ['d-flex', 'flex-column-reverse'],
                                onLocalFormSubmit: () => { },
                                formProps: { onSubmit: (e) => e.preventDefault() }
                            }}>
                            <UniversalInput
                                label={{
                                    text: name === 'phoneNumber' ? 'ssap.input.label.new_phone_number' : 'ssap.input.label.new_email',
                                    labelClasses: ['p-0', 'text-semibold', 'text-left-i']
                                }}
                                errorClasses={['m-0']}
                                inputData={{
                                    isInputRequired: true,
                                    type,
                                    name,
                                    helperClasses: ['center-flex', 'align-content-start', 'align-items-start'],
                                    value,
                                    placeholder: name === 'phoneNumber' ? 'ssap.input.placeholder.phone_number' : 'ssap.input.placeholder.email',
                                    validationType,
                                    currentData: {},
                                    fields: []
                                }}
                                inputActions={{
                                    onFieldChange: onContactChange,
                                    tracker
                                }}
                            />
                        </Form>
                        <ValidationErrorMessage
                            isVisible={checkIfInvalid().errorKey}
                            messageText={checkIfInvalid().errorKey}
                            errorClasses={['m-0']}
                        />
                    </div>
                </div>
                <div className='gi-modal__footer'>
                    <div className='grid-row controls button__container justify-content-between'>
                        <GIButton
                            id='cancel_button'
                            labelId={'button.cancel'}
                            onClickHandler={handleModalClose}
                            addlClassNames={'gi-button__outline'}
                        />
                        <GIButton
                            id='otp_send_button'
                            labelId={'button.continue'}
                            onClickHandler={onContactSubmit}
                            isDisabled={checkIfInvalid().invalid}
                        />
                    </div>
                </div>
            </div>
        )
    }

    const validateOTPStep = () => {
        return (
            <div>
                <div className="gi-modal__body">
                    <p className='mt-0'>
                        <FormattedMessage id='modals.body.verify_otp' values={{ value: formattedValue, contact: contactValue.toLowerCase() }} />
                    </p>
                    <label className='text-semibold'>
                        <FormattedMessage id='ssap.input.label.otp' />
                    </label>

                    <Form formName={otp_form_name} formProps={{ onSubmit: (e) => e.preventDefault() }}>
                        <div className='display--flex'>
                            <div className='gi-modal__input'
                                style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', marginRight: "10px" }}>
                                {otp.map((value, index) => (
                                    <input
                                        key={index}
                                        ref={(input) => (inputRefs.current[index] = input)}
                                        id={`otp${index}`}
                                        value={value}
                                        onChange={(e) => handleChange(index, e)}
                                        onClick={() => handleClick(index)}
                                        onKeyDown={(e) => handleKeyDown(index, e)}
                                        onPaste={handlePaste}
                                        maxLength={1}
                                        style={{
                                            width: '3.5rem',
                                            height: '3.5rem',
                                            margin: '0.5rem',
                                            textAlign: 'center',
                                            fontSize: '1.5rem',
                                            fontFamily: 'sans-serif',
                                            borderRadius: '6px',
                                            borderWidth: '1px',
                                        }}
                                    />
                                ))}
                            </div>
                            <GIButton
                                id='otp_verify_button'
                                labelId={'button.submit'}
                                onClickHandler={onOTPVerify}
                                isDisabled={otp.join('').length < OTP_LENGTH || !!otpError}
                            />
                        </div>
                    </Form>

                    {otpError && <span className='text-red'>
                        <FormattedMessage id={otpError} />
                    </span>}

                    <p>
                        <FormattedMessage id='modals.body.no_otp_received' /><span> </span>
                        <span data-test-id='resend-otp-btn' className='btn-link pointer' onClick={onOTPSend}>
                            {isPhone && resendViaCallText ? resendViaCallText : <FormattedMessage id='modals.body.resend' />}
                        </span>
                    </p>
                </div>
                <div className='gi-modal__footer'>
                    <div className={`grid-row controls button__container ${type ? 'justify-content-between' : ''}`}>
                        {type && <GIButton
                            id='back_button'
                            labelId={'button.back'}
                            onClickHandler={() => setOtpStep(false)}
                        />}
                        <GIButton
                            id='cancel_button'
                            labelId={'button.cancel'}
                            onClickHandler={handleModalClose}
                            addlClassNames={'gi-button__outline'}
                        />
                    </div>
                </div>
            </div>
        )
    }

    return (
        <Fragment >
            <div className='gi-modal__header__head ' >
                <ModalHeader
                    {...{
                        ...modalHeaderProps,
                        hideModalCallback: handleModalClose,
                        modalHeaderTextData: [
                            modalHeaderProps.modalHeaderTextData,
                            { value: otpStep ? formattedValue : undefined }
                        ]
                    } }
                />
            </div>
            {
                !otpStep ? updateContactStep() : validateOTPStep()
            }
        </Fragment >
    );
};

export default ContactChangeModal;